import React, { useContext, forwardRef } from "react";
import PropTypes from "prop-types";
import { ThemeContext } from "../Theme";

import styles from "./Button.scss";
import ButtonIcon from "./ButtonIcon";

export const ButtonDefaultTagName = "button";

export const Button = forwardRef(
  ({ children, tag, variants, customClass, hasMinWidth, ...props }, ref) => {
    const context = useContext(ThemeContext);

    const tagProps = {
      type: tag === "button" ? "button" : undefined,
      ...props,
    };
    let classNames = variants;
    const CustomTag = tag;

    if (!classNames) classNames = styles.button;

    if (typeof classNames !== "string") classNames = classNames.join(" ");

    if (context) classNames += ` ${styles[context]}`;

    if (customClass) classNames += ` ${customClass}`;

    if (hasMinWidth) classNames += ` ${styles["min-width"]}`;

    return (
      <CustomTag className={classNames} ref={ref} {...tagProps}>
        {children &&
          React.Children.map(children, (child) => {
            // To wrap the actual button text we have to check if the type is undefined because the button text
            // does not have a specific component
            if (child.type === undefined) {
              return <span className={styles.label}>{child}</span>;
            }

            return child;
          })}
      </CustomTag>
    );
  }
);

Button.propTypes = {
  tag: PropTypes.string,
  customClass: PropTypes.string,
  variants: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  hasMinWidth: PropTypes.bool,
};

Button.defaultProps = {
  tag: ButtonDefaultTagName,
  variants: "",
  customClass: null,
  hasMinWidth: false,
};

Button.Variant = {
  small: styles.small,
  outline: styles.outline,
  icon: styles.onlyIcon,
  onlyIconUntilS: styles.onlyIconUntilS,
  onlyIconUntilM: styles.onlyIconUntilM,
  onlyIconUntilL: styles.onlyIconUntilL,
  onlyIconUntilXL: styles.onlyIconUntilXL,
  roundPrimary: styles.roundPrimary,
  roundOutline: styles.roundOutline,
  clean: styles.clean,
  round: styles.round,
  roundBoldOutline: styles.roundBoldOutline,
  active: styles.active,
  reactangle: styles.reactangle,
  inverted: styles.inverted,
  full: styles.full,
  padded: styles.padded,
  bold: styles.bold,
  uppercase: styles.uppercase,
  labeledVertical: styles.labeledVertical,
  bare: styles.bare,
  secondary: styles.secondary,
  responsive: styles.responsive,
  slider: styles.slider,
};

Button.Icon = ButtonIcon;

Button.displayName = "Button";

Button.contextType = ThemeContext;

export default Button;
